/**
 * Balanced auto-wrap for UNIFORM-style captions (Studio Phase 5).
 *
 * The reference grammar the operator wants (see the Phase 5 section of
 * docs/superpowers/specs/2026-07-15-studio-iterate-editor-design.md): long
 * cues break into SHORT, VISUALLY BALANCED stacked lines — never one wide
 * line, never a big-punch second line. Emphasis is `<em>…</em>` color spans
 * that must survive the wrap (a span may cross a line break; it splits into
 * one span per line).
 *
 * PURE module — no React, no DOM. It is imported by BOTH the Remotion
 * composition (src/remotion/CaptionLine.tsx) and anything that wants to
 * predict the breaks: the render and the live preview produce the same
 * lines by construction because they call the same function.
 *
 * Width model: the same glyph-ratio estimate the rest of the codebase uses
 * (CaptionLine's one-line-fit heuristic, the translation budgets in
 * src/lib/captionLayout.ts): usable px = canvas − 2·sidePadding − 2·boxPadX,
 * approx char width = fontSizePx · glyphRatio, budget = floor(usable/char).
 * Lengths are measured on the VISIBLE text (em tags stripped).
 */
import { splitEmphasis } from "./srt.ts";

// ---------------------------------------------------------------------------
// Budget
// ---------------------------------------------------------------------------

export interface UniformWrapBudget {
  fontSizePx: number;
  sidePaddingPx: number;
  /** cap.box?.padXPx — 0 when the caption has no backing box. */
  boxPadXPx?: number;
  /** Approx glyph advance in em. Default 0.6 (Arabic — the legacy value). */
  glyphRatio?: number;
  /** Default 2160 (the render canvas). */
  canvasWidthPx?: number;
}

/** Max characters per rendered line under the uniform budget. Never < 1. */
export function uniformMaxChars(b: UniformWrapBudget): number {
  const canvas = b.canvasWidthPx ?? 2160;
  const usable = canvas - 2 * b.sidePaddingPx - 2 * (b.boxPadXPx ?? 0);
  const approxCharW = b.fontSizePx * (b.glyphRatio ?? 0.6);
  if (!(approxCharW > 0)) return 1;
  return Math.max(1, Math.floor(usable / approxCharW));
}

// ---------------------------------------------------------------------------
// Word model — words carry their em state so spans survive re-lining
// ---------------------------------------------------------------------------

export interface WordFragment {
  text: string;
  em: boolean;
  /** Explicit per-span color (#RRGGBB) carried through re-wrapping so an
   *  operator-colored span survives the measured line-break. Absent → the
   *  emphasized default. Only meaningful when `em` is true. */
  color?: string;
}

export interface WordToken {
  /** Visible text (no tags) — what the width measurement counts. */
  plain: string;
  /** The word's runs of em/non-em (a hand-typed span may start mid-word). */
  frags: WordFragment[];
}

/** Split one explicit line into words, preserving per-fragment em flags.
 *  Exported for the karaoke/word-stagger renderer (phase 2), which needs
 *  the same word model the wrap uses. */
export function tokenizeWords(line: string): WordToken[] {
  const words: WordToken[] = [];
  let current: WordToken | null = null;
  for (const seg of splitEmphasis(line)) {
    for (const part of seg.text.split(/(\s+)/)) {
      if (part === "") continue;
      if (/^\s+$/.test(part)) {
        current = null;
        continue;
      }
      if (!current) {
        current = { plain: "", frags: [] };
        words.push(current);
      }
      current.plain += part;
      const last = current.frags[current.frags.length - 1];
      // Merge only when BOTH em flag AND color match — a color change starts
      // a new fragment so the span survives re-serialization.
      if (last && last.em === seg.emphasized && last.color === seg.color) last.text += part;
      else current.frags.push({ text: part, em: seg.emphasized, ...(seg.color ? { color: seg.color } : {}) });
    }
  }
  return words;
}

/** The `<em>` open tag a fragment needs — plain accent or a colored span.
 *  Non-em fragments have no tag (empty string). */
const openTagFor = (f: WordFragment): string =>
  !f.em ? "" : f.color ? `<em c="${f.color}">` : "<em>";

/** Re-serialize a run of words into one line, re-emitting `<em>`/`<em c="…">`
 *  tags. A space between two same-span emphasized words stays INSIDE the
 *  span, so `<em>par la peur</em>` survives a round-trip as one span; a color
 *  CHANGE (or em↔non-em) closes and reopens. */
export function serializeWords(words: WordToken[]): string {
  let out = "";
  // The currently-open em tag string ("" = none open), so a color change is
  // a real close+reopen, not a silent continuation.
  let open = "";
  words.forEach((w, i) => {
    if (i > 0) {
      const nextTag = openTagFor(w.frags[0] ?? { text: "", em: false });
      // Close before the space only when the next word doesn't continue the
      // exact same span (keeps a multi-word span's internal spaces inside it).
      if (open && nextTag !== open) {
        out += "</em>";
        open = "";
      }
      out += " ";
    }
    for (const f of w.frags) {
      const tag = openTagFor(f);
      if (tag !== open) {
        if (open) out += "</em>";
        if (tag) out += tag;
        open = tag;
      }
      out += f.text;
    }
  });
  if (open) out += "</em>";
  return out;
}

/** Visible length of words[from..to) joined with single spaces. */
function spanLength(cum: number[], from: number, to: number): number {
  // cum[i] = total plain chars of words[0..i); spaces = (count − 1)
  const chars = cum[to]! - cum[from]!;
  const count = to - from;
  return count <= 0 ? 0 : chars + (count - 1);
}

// ---------------------------------------------------------------------------
// Balanced wrap
// ---------------------------------------------------------------------------

/**
 * Wrap ONE explicit line into 1, 2 or 3 balanced lines at word boundaries.
 * Deterministic: candidates are scanned in ascending break order and only a
 * STRICTLY better (smaller max line, then smaller imbalance) candidate
 * replaces the current best. A line that can't fit even in 3 (a single
 * over-budget word) returns the best-effort 3-way split — the renderer
 * overflows gracefully rather than dropping text.
 */
function wrapLine(line: string, maxChars: number): string[] {
  const words = tokenizeWords(line);
  if (words.length === 0) return [];
  const cum: number[] = [0];
  for (const w of words) cum.push(cum[cum.length - 1]! + w.plain.length);

  const total = spanLength(cum, 0, words.length);
  if (total <= maxChars || words.length === 1) return [serializeWords(words)];

  // ---- 2 lines: pick the break minimizing (max length, imbalance) ----
  let best2: { i: number; max: number; diff: number } | null = null;
  for (let i = 1; i < words.length; i++) {
    const a = spanLength(cum, 0, i);
    const b = spanLength(cum, i, words.length);
    const cand = { i, max: Math.max(a, b), diff: Math.abs(a - b) };
    if (!best2 || cand.max < best2.max || (cand.max === best2.max && cand.diff < best2.diff)) {
      best2 = cand;
    }
  }
  if (best2 && best2.max <= maxChars) {
    return [
      serializeWords(words.slice(0, best2.i)),
      serializeWords(words.slice(best2.i)),
    ];
  }

  // ---- 3 lines (only when 2 can't fit) ----
  if (words.length >= 3) {
    let best3: { i: number; j: number; max: number; diff: number } | null = null;
    for (let i = 1; i < words.length - 1; i++) {
      for (let j = i + 1; j < words.length; j++) {
        const a = spanLength(cum, 0, i);
        const b = spanLength(cum, i, j);
        const c = spanLength(cum, j, words.length);
        const max = Math.max(a, b, c);
        const diff = Math.max(a, b, c) - Math.min(a, b, c);
        if (!best3 || max < best3.max || (max === best3.max && diff < best3.diff)) {
          best3 = { i, j, max, diff };
        }
      }
    }
    if (best3) {
      return [
        serializeWords(words.slice(0, best3.i)),
        serializeWords(words.slice(best3.i, best3.j)),
        serializeWords(words.slice(best3.j)),
      ];
    }
  }

  // 2 words that don't fit → best-effort 2-way split.
  return [
    serializeWords(words.slice(0, best2!.i)),
    serializeWords(words.slice(best2!.i)),
  ];
}

/** Hard cap on rendered rows: rows past `maxLines` are folded into the last
 *  allowed row (joined with a space). The text is never dropped — a too-long
 *  last row overflows visibly, which the operator can fix, instead of a
 *  third/fourth line silently appearing. */
export function capLines(lines: string[], maxLines: number): string[] {
  const n = Math.max(1, Math.floor(maxLines));
  if (lines.length <= n) return lines;
  const head = lines.slice(0, n - 1);
  const tail = lines.slice(n - 1).join(" ");
  return [...head, tail];
}

/**
 * Wrap a cue's full text for the uniform caption style.
 *
 * Explicit `\n` ALWAYS wins: each authored line is kept as its own segment
 * (never re-joined with its neighbours) and only wraps further when IT
 * overflows the budget. Returns the final rendered lines, `<em>` spans
 * preserved (split per line when a span crosses a break).
 *
 * `maxLines`, when given, hard-caps the rendered row count (see `capLines`)
 * — absent, the founding behaviour (uncapped 3-way split per authored line)
 * is unchanged.
 */
export function wrapUniformCue(text: string, maxChars: number, maxLines?: number): string[] {
  const rows = text
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean)
    .flatMap((line) => wrapLine(line, maxChars));
  return maxLines ? capLines(rows, maxLines) : rows;
}

// ---------------------------------------------------------------------------
// Width-measured wrap (text engine v2 — spec 2026-07-18)
// ---------------------------------------------------------------------------

/** Width in px of a visible string. See src/lib/textMeasure.ts for the
 *  canvas-backed and estimate implementations. */
export type MeasureFn = (text: string) => number;

/** Px width of words[from..to) joined by single spaces. */
function spanWidth(
  cumW: number[],
  spaceW: number,
  from: number,
  to: number,
): number {
  const count = to - from;
  if (count <= 0) return 0;
  return cumW[to]! - cumW[from]! + (count - 1) * spaceW;
}

/**
 * Wrap ONE explicit line to a pixel budget using REAL measured widths —
 * the same balanced 1/2/3-line search as wrapLine, plus an unbounded
 * greedy fallback when even 3 lines can't fit (tiny width / huge font):
 * every line packs words until the next would overflow, min one word per
 * line. Text is never dropped.
 */
function wrapLineToWidth(line: string, maxWidthPx: number, measure: MeasureFn, maxLines?: number): string[] {
  const words = tokenizeWords(line);
  if (words.length === 0) return [];
  const spaceW = Math.max(0, measure("   ") - 2 * measure(" "));
  const cumW: number[] = [0];
  for (const w of words) cumW.push(cumW[cumW.length - 1]! + measure(w.plain));

  const total = spanWidth(cumW, spaceW, 0, words.length);
  if (total <= maxWidthPx || words.length === 1) return [serializeWords(words)];

  // ---- 2 lines ---- (skipped when the caller caps at 1 — e.g. a punch
  // line that must stay on its own single row; it then greedy-collapses to 1.)
  // Among the splits that FIT, prefer the MOST BALANCED (smallest width
  // difference) so the two lines read as similar lengths — operator rule
  // "they should be a similar length… never a long first line and a very short
  // second line". Falls through to 3-line/greedy only when no 2-line fits.
  if (maxLines === undefined || maxLines >= 2) {
    let bal: { i: number; diff: number } | null = null;
    for (let i = 1; i < words.length; i++) {
      const a = spanWidth(cumW, spaceW, 0, i);
      const b = spanWidth(cumW, spaceW, i, words.length);
      if (Math.max(a, b) > maxWidthPx) continue; // this split doesn't fit
      const diff = Math.abs(a - b);
      if (!bal || diff < bal.diff) bal = { i, diff };
    }
    if (bal) {
      return [
        serializeWords(words.slice(0, bal.i)),
        serializeWords(words.slice(bal.i)),
      ];
    }
  }

  // ---- 3 lines ---- (skipped when the caller caps at 2 — operator rule
  // "never more than two lines"; a too-wide cue then greedy-collapses to 2.)
  if ((maxLines === undefined || maxLines >= 3) && words.length >= 3) {
    let best3: { i: number; j: number; max: number; diff: number } | null = null;
    for (let i = 1; i < words.length - 1; i++) {
      for (let j = i + 1; j < words.length; j++) {
        const a = spanWidth(cumW, spaceW, 0, i);
        const b = spanWidth(cumW, spaceW, i, j);
        const c = spanWidth(cumW, spaceW, j, words.length);
        const max = Math.max(a, b, c);
        const diff = max - Math.min(a, b, c);
        if (!best3 || max < best3.max || (max === best3.max && diff < best3.diff)) {
          best3 = { i, j, max, diff };
        }
      }
    }
    if (best3 && best3.max <= maxWidthPx) {
      return [
        serializeWords(words.slice(0, best3.i)),
        serializeWords(words.slice(best3.i, best3.j)),
        serializeWords(words.slice(best3.j)),
      ];
    }
  }

  // ---- greedy unbounded (never drop text) ----
  const out: string[] = [];
  let start = 0;
  for (let i = 1; i <= words.length; i++) {
    const atEnd = i === words.length;
    const overflows = spanWidth(cumW, spaceW, start, i) > maxWidthPx && i - start > 1;
    if (overflows) {
      out.push(serializeWords(words.slice(start, i - 1)));
      start = i - 1;
    }
    if (atEnd) out.push(serializeWords(words.slice(start)));
  }
  // Hard line cap (operator rule: max 2 lines). Merge any overflow rows into
  // the last allowed row — a too-long line may then exceed the width, but it
  // NEVER becomes a third line. Only reachable when maxLines is passed (auto).
  if (maxLines !== undefined && out.length > maxLines) {
    const capped = out.slice(0, maxLines - 1);
    capped.push(out.slice(maxLines - 1).join(" "));
    return capped;
  }
  return out;
}

/**
 * Width-measured cue wrap (wrapMode "auto"). Explicit `\n` ALWAYS wins —
 * authored lines never re-join; each wraps further only when IT overflows
 * the pixel budget. `<em>` spans survive (split per line across breaks).
 */
export function wrapCueToWidth(text: string, maxWidthPx: number, measure: MeasureFn, maxLines?: number): string[] {
  return text
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean)
    .flatMap((line) => wrapLineToWidth(line, maxWidthPx, measure, maxLines));
}

/**
 * Wrap with a PREFERRED row count that yields rather than overflow the frame.
 *
 * A channel that never pinned `maxLines` still renders at a default of two
 * rows — the founding look, and right for every cue that fits in two. A cue
 * that CANNOT fit in two (a long sentence at a big caption size) is a
 * different question, and the old answer was to merge the residue into the
 * last row and let it run off both edges of the video: the operator picked
 * the caption size the product itself recommends and watched a word leave the
 * frame (QA 2026-09-16, B1; spec §1.9 "a caption never overflows the frame in
 * any preview we show").
 *
 * So the cap is a PREFERENCE, not a promise: the capped wrap wins whenever it
 * fits, and only when it does not does the uncapped wrap take over and give
 * the sentence the rows it needs. A cue that fits today wraps EXACTLY as it
 * did — the only cues that change are the ones already running off the frame.
 *
 * A channel that pins `maxLines` is untouched: there the cap IS the promise
 * (`wrapCueTotalToWidth`), because the operator asked for it by number.
 */
export function wrapCuePreferring(
  text: string,
  maxWidthPx: number,
  measure: MeasureFn,
  preferred: number,
): string[] {
  const capped = wrapCueToWidth(text, maxWidthPx, measure, preferred);
  if (capped.every((row) => renderedRowWidth(row, measure) <= maxWidthPx)) return capped;
  return wrapCueToWidth(text, maxWidthPx, measure);
}

/**
 * Last-line size (spec 2026-09-16 onboarding round 2 §2.6): the LAST row of a
 * multi-row cue renders at a scaled size, so the rows must be chosen with
 * that row's real width in mind. Rows come from the base wrap; only when the
 * last row, drawn at the scaled size, would exceed `maxWidthPx` is the scaled
 * wrap consulted — and its rows are taken ONLY when they make the last row
 * narrower (under the scaled measurer) than the base's. Under a pinned cap
 * (`capLines` / `wrapCueTotalToWidth` / `wrapUniformCue(…, maxLines)`) the
 * scaled re-wrap breaks earlier and folds MORE words into the merged last
 * row — wider, not narrower — and reshuffles row 1 for nothing; lines = 2 is
 * THE use case, so a re-wrap that does not help is discarded. A cue that
 * wraps to one row keeps the base rows untouched: nothing scales on it.
 *
 * The wraps are thunks so the scaled one runs only when needed — the common
 * case is exactly ONE wrap, the founding one. Deterministic and measurer-
 * driven, so the preview and the render pick the same rows.
 */
export function rowsWithScaledLastLine(
  wrapBase: () => string[],
  wrapScaled: () => string[],
  maxWidthPx: number,
  measureScaled: MeasureFn,
): string[] {
  const rows = wrapBase();
  if (rows.length < 2) return rows;
  const baseLastW = renderedRowWidth(rows[rows.length - 1]!, measureScaled);
  if (baseLastW <= maxWidthPx) return rows;
  const scaled = wrapScaled();
  if (scaled.length === 0) return rows;
  const scaledLastW = renderedRowWidth(scaled[scaled.length - 1]!, measureScaled);
  return scaledLastW < baseLastW ? scaled : rows;
}

/** A wrapped row's drawn width — the same arithmetic `wrapLineToWidth` used to
 *  choose the break, so `<em>` markup is measured as the words it wraps and
 *  not as literal characters. */
export function renderedRowWidth(row: string, measure: MeasureFn): number {
  const words = tokenizeWords(row);
  if (words.length === 0) return 0;
  const spaceW = Math.max(0, measure("   ") - 2 * measure(" "));
  let w = 0;
  for (const word of words) w += measure(word.plain);
  return w + (words.length - 1) * spaceW;
}

/**
 * Width-measured cue wrap where `maxLines` is a TOTAL row cap across the whole
 * cue — spec 2026-09-15-brand-study-design.md §4: "hard cap; overflow merges
 * into the last row".
 *
 * `wrapCueToWidth` treats an authored `\n` as inviolable and applies the cap
 * PER authored line, so a pre-broken SRT cue ("un système\nentier où les gens
 * ont honte de") renders `authoredLines × maxLines` rows — three rows under a
 * cap of two. When the channel pins `maxLines`, the cap is the promise, not the
 * authored break: the authored lines are re-joined, re-wrapped as one cue, and
 * `capLines` folds any residue into the last allowed row (text is never
 * dropped; a too-long last row overflows visibly).
 *
 * With `maxLines` absent this is byte-identically `wrapCueToWidth` — the
 * founding per-authored-line behaviour, which is what `wrapMode:"auto"`
 * channels without an explicit `maxLines` must keep.
 */
export function wrapCueTotalToWidth(
  text: string,
  maxWidthPx: number,
  measure: MeasureFn,
  maxLines?: number,
): string[] {
  if (maxLines === undefined) return wrapCueToWidth(text, maxWidthPx, measure);
  const joined = text
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean)
    .join(" ");
  return capLines(wrapCueToWidth(joined, maxWidthPx, measure, maxLines), maxLines);
}
